import camelCase from 'lodash/camelCase';
import isNaN from 'lodash/isNaN';
import isString from 'lodash/isString';
import parseInt from 'lodash/parseInt';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import upperFirst from 'lodash/upperFirst';
import map from 'lodash/map';
import get from 'lodash/get';
import join from 'lodash/join';
import { LATIN_CHARACTERS } from '../const/text.const';
import { LOG_TYPE } from '../const/logger.const';
import { logger } from '../logger/logger.class';
/**
 * Turns a string into human readable 'Title Case'
 * Capitalizes first letter of each word.
 * @param value string
 * @example
 * 'myString' => 'My String'
 * 'my_string' => 'My String'
 * 'MY_STRING' => 'My String'
 * 'my string' => 'My String'
 * 'My string' => 'My String'
 * 'My String' => 'My String'
 *
 * Corresponding Vue filter: `titleCase`
 */
export function titleCase(value) {
    if (!value || !isString(value)) {
        logger.message(`Incorrect type or value! ${value}`, LOG_TYPE.warning);
        return '';
    }
    const camelCased = camelCase(value);
    return startCase(camelCased);
}
/**
 * Turns a string' into human readable 'Sentence case'
 * Capitalizes first letter of the string.
 * @param value string
 * @example
 * 'myString' => 'My string'
 * 'my_string' => 'My string'
 * 'MY_STRING' => 'My string'
 * 'my string' => 'My string'
 * 'My string' => 'My string'
 * 'My String' => 'My string'
 *
 * Corresponding Vue filter: `sentenceCase`
 */
export function sentenceCase(value) {
    if (isString(value) && !isNaN(parseInt(value, 10))) {
        logger.message(`String number value of ${value} was left intact`, LOG_TYPE.warning);
        return value;
    }
    if (!value || !isString(value)) {
        logger.message(`Incorrect type or value! ${value}`, LOG_TYPE.warning);
        return '';
    }
    const normalized = toLower(startCase(camelCase(value)));
    return upperFirst(normalized);
}
/**
 * Turns any string' into a 'PascalCase' string
 * @param value string
 * @example
 * 'myString' => 'MyString'
 * 'my_string' => 'MyString'
 * 'MY_STRING' => 'MyString'
 * 'my string' => 'MyString'
 * 'My string' => 'MyString'
 * 'My String' => 'MyString'
 *
 * Corresponding Vue filter: `pascalCase`
 */
export function pascalCase(value) {
    if (!value || !isString(value)) {
        logger.message(`Incorrect type or value! ${value}`, LOG_TYPE.warning);
        return '';
    }
    const camelCased = camelCase(value);
    return upperFirst(camelCased);
}
/**
 * Converts diacritics characters to basic latin alphabet
 * @param text string
 * @example
 * normalizeDiacritics('HÜé') => 'HUe'
 * @returns string
 */
export function normalizeDiacritics(text) {
    if (text && text.match(/[^(a-z)|(A-Z)|(0-9)]/)) {
        const parsedTextArray = map(text, letter => {
            return get(LATIN_CHARACTERS, letter, letter);
        });
        return join(parsedTextArray, '');
    }
    return text || '';
}
/**
 * Lowercases and normalizes whitespaces and diacritics of a string
 * @param text string
 * @example
 * normalizeText('A   B\rÜ') => 'a b u'
 * @returns string
 */
export function normalizeText(text) {
    if (!text || !isString(text)) {
        logger.message(`Incorrect type or empty value ${text}`, LOG_TYPE.warning);
        return text || '';
    }
    return toLower(normalizeDiacritics(text)
        .replace(/[\u0080-\uF8FF]/g, '')
        .replace(/\s+/g, ' '));
}
/**
 * Transforms given text by making a replacement of given characters in a string
 * @param text string
 * @param splitBy string - delimiter characted to split the text by
 * @param joinBy string - character to replace `splitBy` with
 * @returns string | unknown
 */
export function transformText(text, splitBy = '', joinBy = '') {
    if (!isString(text)) {
        logger.message(`Incorrect value provided: ${text}`, LOG_TYPE.warning);
        return text;
    }
    if (!text) {
        logger.message(`Falsy value provided: ${text}`, LOG_TYPE.warning);
        return '';
    }
    return text.split(splitBy).join(joinBy);
}
